<template>
	<v-row align="center" justify="center">
		<v-col>
			<v-form class="login-form" method="POST" @submit.prevent="login">
				<v-card flat tile width="400" class="mx-auto">
					<v-card-title>Login</v-card-title>
					<img src="https://pharmamonitorexpress.se/images/pmx_logo_small.png" alt="" />
					<p class="text-center red--text">{{ errorText }}</p>
					<v-text-field
						v-model="email"
						:rules="emailRules"
						:append-icon="'mdi-email'"
						label="Email adress"
						required
						outlined
						class="mx-4"
					/>

					<v-text-field
						v-model="password"
						:rules="[passwordRules.required]"
						:append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
						:type="showPassword ? 'text' : 'password'"
						label="Password"
						required
						outlined
						class="mx-4"
						@click:append="showPassword = !showPassword"
					/>

					<v-card-text class="pb-1 pt-0">
						<v-btn block type="submit" color="grey" :loading="isLoading">Login</v-btn>
					</v-card-text>
					<p class="text-right mr-4" id="forgot-password">
						<router-link class="text-decoration-none" to="/forgot-password">
							Forgot your password?
						</router-link>
					</p>
					<div id="footer" class="mx-4 mb-2 mt-4 text-center grey--text text--darken-4">
						By logging in to Pharma Monitor Express, I confirm that I accept<br />
						Bonnier Pharma Insights
						<a
							style="text-decoration: none"
							target="_blank"
							href="https://www.bonnierpharmainsights.se/personuppgiftspolicy/"
							>personal privacy policy.
						</a>
					</div>
				</v-card>
			</v-form>
		</v-col>
	</v-row>
</template>

<script>
	import UserAPI from '@/api/user';
	import CryptoJS from 'crypto-js';

	/**
	 * @module LoginView
	 *
	 * @vue-data {string} [errorText=''] - Display error text when there is one
	 * @vue-data {boolean} [isLoading=false] - Display progress on button
	 * @vue-data {boolean} [showPassword=false] - Show password text
	 * @vue-data {string} [email=''] - The user email
	 * @vue-data {string} [password=''] - The user password
	 * @vue-data {array<boolean>} [emailRules=true] - Rules for email validation
	 * @vue-data {object<boolean>} [passwordRules=true] - Rules for password validation
	 */

	export default {
		data: () => ({
			errorText: '',
			isLoading: false,
			showPassword: false,
			email: '',
			password: '',
			emailRules: [
				value => !!value || 'Email adress is required.',
				value => /.+@.+/.test(value) || 'Email adress must be valid.',
			],
			passwordRules: {
				required: value => !!value || 'Password is required.',
			},
		}),
		methods: {
			/* 
                POST   /api/v1/login
            */

           /**
            * @async 
            * 
            * @returns {void}
            * 
            * @description 
            * - @HTTP 200
            * - Display progress on button
            * - Get user data and encrypt it 
            * - Delete user ID and company ID since we don't want it to be shown in local storage 
            * - Add the encrypted data to local storage
            * - Send user to dashboard page 
            * <br>&nbsp;
            * - @HTTP 401 
            * - Display error message 
            */
			async login() {
				try {
					this.isLoading = true;
					const { data } = await UserAPI.login(this.email, this.password);

					const userID = JSON.stringify(data.user_id);
					const companyID = JSON.stringify(data.company_id);

					const encryptedUserID = CryptoJS.AES.encrypt(
						userID,
						process.env.VUE_APP_GOOGLE_SITE_KEY
					).toString();
					const encryptedCompanyID = CryptoJS.AES.encrypt(
						companyID,
						process.env.VUE_APP_GOOGLE_SITE_KEY
					).toString();

					if (data.user_id || data.company_id) {
						delete data.user_id;
						delete data.company_id;
					}

					localStorage.setItem('user', JSON.stringify(data));
					localStorage.setItem('userID', encryptedUserID);
					localStorage.setItem('companyID', encryptedCompanyID);

					this.$router.push({ path: '/dashboard' });
				} catch (error) {
					if (401 === error.response.status) {
						this.errorText = 'You have entered an invalid email or password.';
					}
				} finally {
					this.isLoading = false;
					setTimeout(() => {
						this.errorText = '';
					}, 3000);
				}
			},
		},
	};
</script>

<style lang="scss" scoped>
	.login-form {
		.v-card {
			border: 1px solid grey;
			border-radius: 25px 0 !important;
		}

		.v-card__title {
			margin: 50px 0 20px 0;
			justify-content: center;
			background-color: #9e9e9e;
			color: #fff;
			border-radius: 0 !important;
		}

		.v-card__text {
			.v-btn {
				color: #fff;
			}
		}

		img {
			display: block;
			margin: 0 auto 16px auto;
		}
	}

	#forgot-password {
		font-size: 0.795rem;
	}

	#footer {
		font-size: 0.675rem;
		line-height: 0.765rem;
	}
</style>
